<template>
    <v-dialog v-model="dialogVisible" max-width="600px">
        <v-card>
            <v-card-title>
                <span class="headline">{{
                    isEdited
                        ? $t("languages.edit_language")
                        : $t("languages.add_language")
                }}</span>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="formData.name"
                                    :label="$t('languages.language')"
                                    :rules="languageRules"
                                    required
                                    :disabled="formData.code === 'cz'"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="formData.country"
                                    :items="countryItems"
                                    item-text="název"
                                    item-value="name"
                                    :rules="countryRules"
                                    :label="$t('languages.country')"
                                    required
                                    :disabled="formData.code === 'cz'"
                                    @change="updateCountryCode"
                                >
                                    <template v-slot:item="{ item }">
                                        <span
                                            :class="'fi fi-' + item.code"
                                            style="margin-right: 8px"
                                        ></span>
                                        {{ item.název }}
                                    </template>
                                    <template v-slot:selection="{ item }">
                                        <span
                                            :class="'fi fi-' + item.code"
                                            style="margin-right: 8px"
                                        ></span>
                                        {{ item.název }}
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDialog">
                    {{ $t("general.cancel") }}
                </v-btn>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="saveLanguage"
                    :disabled="!valid || formData.code === 'cz'"
                >
                    {{ $t("general.save") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import countries from "../data/countries";

export default {
    props: {
        language: {
            type: Object,
            required: true,
        },
        visible: {
            type: Boolean,
            default: false,
        },
        isEdited: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            valid: true,
            formData: {
                name: "",
                country: "",
                code: "",
            },
            languageRules: [
                (v) => !!v || this.$t("languages.language_required"),
                (v) =>
                    (v && v.length <= 255) ||
                    this.$t("languages.language_max_length"),
            ],
            countryRules: [
                (v) => !!v || this.$t("languages.country_required"),
                (v) =>
                    (v && v.length <= 255) ||
                    this.$t("languages.country_max_length"),
            ],
            codeRules: [
                (v) => !!v || this.$t("languages.code_required"),
                (v) =>
                    (v && v.length <= 10) ||
                    this.$t("languages.code_max_length"),
            ],
        };
    },

    computed: {
        dialogVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                }
            },
        },
        countryItems() {
            return Array.isArray(countries[0]) ? countries[0] : countries;
        },
    },

    watch: {
        visible(val) {
            if (val && this.isEdited) {
                this.formData = { ...this.language };
                if (this.language.country) {
                    this.updateCountryCode(this.language.country);
                }
            } else if (val) {
                this.resetForm();
            }
        },
    },

    methods: {
        updateCountryCode(selectedCountryName) {
            const selectedCountry = this.countryItems.find(
                (country) =>
                    country.name === selectedCountryName ||
                    country.název === selectedCountryName
            );

            if (selectedCountry) {
                this.formData.code = selectedCountry.code;
            } else {
                console.log("Country not found:", selectedCountryName);
                this.formData.code = "";
            }
        },

        resetForm() {
            this.formData = {
                name: "",
                country: "",
                code: "",
                is_active: true,
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        closeDialog() {
            this.$emit("close");
            this.resetForm();
        },

        async saveLanguage() {
            if (this.$refs.form.validate()) {
                try {
                    if (this.isEdited) {
                        await this.updateLanguage();
                    } else {
                        await this.createLanguage();
                    }
                    this.closeDialog();
                } catch (error) {
                    console.error(error);
                    this.$emit("snackMessage", "general.error", "error");
                }
            }
        },

        async createLanguage() {
            const response = await axios.post("/languages", this.formData, {
                headers: {
                    Authorization:
                        "Bearer " +
                        this.$store.state.tokenData.user.access_token,
                },
            });
            this.$emit("addLanguage", response.data.language);
            this.$emit("snackMessage", "languages.language_added", "success");
        },

        async updateLanguage() {
            const response = await axios.put(
                `/languages/${this.language.id}`,
                this.formData,
                {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                }
            );
            this.$emit("updateLanguage", response.data.language);
            this.$emit("snackMessage", "languages.language_saved", "success");
        },
    },
};
</script>
